import { store } from '../redux/store';
import { loginWithGoogle, registerWithGoogle } from '../redux/auth/authOperations';
import { Notify } from 'notiflix/build/notiflix-notify-aio';
import i18n from '../i18n';

// Google OAuth Configuration
const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '191509139011-ali43lkogpprkgli7jo2bkcrngjesooj.apps.googleusercontent.com';

const missingAccountKeywords = [
  'not found',
  'no user',
  'does not exist',
  'please register',
  'register first',
  'account removed',
  'deleted',
];

const extractErrorMessage = (error) => {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  if (error.response?.data?.message) return String(error.response.data.message);
  if (error.response?.data?.error) return String(error.response.data.error);
  if (error.message) return String(error.message);
  return undefined;
};

const isMissingAccountError = (message) => {
  if (!message) return false;
  const lowered = message.toLowerCase();
  return missingAccountKeywords.some((keyword) => lowered.includes(keyword));
};

const redirectToRegister = () => {
  Notify.failure(i18n.t('auth.user_not_found'));
  Notify.info(i18n.t('auth.please_register_first'));
  setTimeout(() => {
    window.location.href = '/#/';
  }, 1500);
};

class GoogleAuthService {
  constructor() {
    this.isGoogleLoaded = false;
    this.isRegistrationMode = false;
  }

  // Загружаем Google OAuth библиотеку
  async loadGoogleScript() {
    if (this.isGoogleLoaded) return;

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.isGoogleLoaded = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google OAuth script'));
      };

      document.head.appendChild(script);
    });
  }

  // Инициализация Google OAuth
  async initialize() {
    try {
      await this.loadGoogleScript();

      if (typeof window !== 'undefined' && window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: this.handleCredentialResponse.bind(this),
          auto_select: false,
          cancel_on_tap_outside: true,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  // Обработка ответа от Google OAuth
  async handleCredentialResponse(response) {
    try {
      const { credential } = response;

      if (!credential) {
        throw new Error('No credential received from Google');
      }

      // Используем состояние класса для определения режима (логин/регистрация)
      const isRegistration = this.isRegistrationMode;

      // Отправляем ID токен на бэкенд для аутентификации через Redux
      const action = isRegistration
        ? registerWithGoogle(credential)
        : loginWithGoogle(credential);

      const result = await store.dispatch(action);

      if (result.type.endsWith('/fulfilled')) {
        Notify.success(
          isRegistration
            ? i18n.t('oauth.googleRegisterSuccess')
            : i18n.t('oauth.googleSignInSuccess')
        );

        // Отправляем кастомное событие для уведомления о успешной аутентификации
        window.dispatchEvent(new Event('googleAuthSuccess'));
      } else {
        throw new Error('No authentication token received');
      }
    } catch (error) {
      const message = extractErrorMessage(error);
      const isRegistration = this.isRegistrationMode;
      if (!isRegistration && isMissingAccountError(message)) {
        redirectToRegister();
      } else {
        Notify.failure(
          message ||
            (isRegistration
              ? i18n.t('oauth.googleRegisterFailed')
              : i18n.t('oauth.googleSignInFailed'))
        );
      }
    }
  }

  // Запуск процесса входа через Google
  async signIn() {
    try {
      this.isRegistrationMode = false;
      await this.initialize();

      if (typeof window !== 'undefined' && window.google) {
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // Fallback: используем redirect вместо popup
            this.showGoogleRedirect(false);
          }
        });
      } else {
        // Fallback: используем redirect вместо popup
        this.showGoogleRedirect(false);
      }
    } catch (error) {
      Notify.failure(i18n.t('oauth.googleInitSignInFailed'));
    }
  }

  // Fallback: используем redirect для входа
  showGoogleRedirect(isRegistration = false) {
    // Google OAuth не поддерживает хеши в redirect_uri, используем путь без хеша
    const redirectUri =
      process.env.REACT_APP_GOOGLE_REDIRECT_URI ||
      window.location.origin + '/oauth/callback';

    const redirectUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&response_type=id_token&scope=openid%20email%20profile&state=${
      isRegistration ? 'register' : 'login'
    }&nonce=${Math.random().toString(36).substring(2, 15)}`;

    window.location.href = redirectUrl;
  }

  // Запуск процесса регистрации через Google
  async signUp() {
    try {
      this.isRegistrationMode = true;
      await this.initialize();

      if (typeof window !== 'undefined' && window.google) {
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // Fallback: используем redirect
            this.showGoogleRedirect(true);
          }
        });
      } else {
        // Fallback: используем redirect
        this.showGoogleRedirect(true);
      }
    } catch (error) {
      Notify.failure(i18n.t('oauth.googleInitSignUpFailed'));
    }
  }

  // Обработка redirect callback от Google OAuth
  async handleRedirectCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const idToken = urlParams.get('id_token');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      Notify.failure(i18n.t('oauth.googleOAuthError', { error }));
      return;
    }

    if (idToken && state) {
      // Обрабатываем ID токен напрямую (асинхронно)
      await this.handleIdToken(idToken, state);
    }
  }

  // Обработка ID токена от Google OAuth
  async handleIdToken(idToken, state) {
    try {
      // Отправляем данные в Redux через соответствующий action
      // Action сам отправит запрос на бэкенд
      const action = state === 'register'
        ? registerWithGoogle(idToken)
        : loginWithGoogle(idToken);

      const result = await store.dispatch(action);

      if (result.type.endsWith('/fulfilled')) {
        // Показываем успешное сообщение
        Notify.success(
          state === 'register'
            ? i18n.t('oauth.googleRegisterSuccess')
            : i18n.t('oauth.googleSignInSuccess')
        );
      } else {
        // Если action был отклонен, выбрасываем ошибку с сообщением
        const errorMessage = result.error?.message || 'Failed to process authentication';
        throw new Error(errorMessage);
      }
    } catch (error) {
      const message = extractErrorMessage(error);

      // При регистрации не редиректим на регистрацию при ошибке "user not found"
      if (state === 'register') {
        Notify.failure(message || i18n.t('oauth.googleRegisterFailed'));
      } else if (isMissingAccountError(message)) {
        // При логине редиректим на регистрацию, если пользователь не найден
        redirectToRegister();
      } else {
        Notify.failure(message || i18n.t('oauth.googleProcessAuthFailed'));
      }

      // Очищаем URL от параметров OAuth
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }


  // Выход из Google аккаунта
  async signOut() {
    try {
      if (typeof window !== 'undefined' && window.google) {
        window.google.accounts.id.disableAutoSelect();
      }
      Notify.success(i18n.t('oauth.googleSignOutSuccess'));
    } catch (error) {
      Notify.failure(i18n.t('oauth.googleSignOutFailed'));
    }
  }
}

export const googleAuthService = new GoogleAuthService();
export default googleAuthService;

